/**
 *  Main Application component that renders the router.
 * @module app
 */

import { ErrorBoundary } from "@mitratech/north-star";
import { RouterProvider } from "@tanstack/react-router";
import { router } from "./Router";

/**
 * Root Application component that renders the router with error boundary protection.
 *
 * App
 * @returns {JSX.Element} The application wrapped in an ErrorBoundary
 * @module app
 * @category Components
 *
 * @remarks
 * This component serves as the main entry point for the React component tree.
 * It wraps the entire application in an {@link ErrorBoundary} from the North Star
 * design system to catch and display runtime errors gracefully.
 *
 * The component uses TanStack Router's {@link RouterProvider} to handle
 * client-side routing based on the file-based route definitions.
 *
 * @example
 * ```typescript
 * import { App } from './app/Application';
 * import { AppProvider } from './app/Application.provider';
 *
 * <AppProvider app={app}>
 *   <App />
 * </AppProvider>
 * ```
 *
 * @see {@link router} for the router configuration
 * @see {@link AppProvider} for the context provider that wraps this component
 */
export const App = () => {
  return (
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  );
};
